import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DateTime } from "luxon";
import { createEntity } from "../../scripts/helperScripts";
import Checkbox from "../common/Checkbox";
import ExpandDiv from "../common/ExpandDiv";
import FloatingInput from "../common/FloatingInput";
import Select from "../common/Select";
import TextArea from "../common/TextArea";
import TrueInput from "../common/TrueInput";
import {
  crisisReferrals,
  crisisResponder,
  getClientCompanies,
  getContact,
  reactManagementUsers,
  sendEmailMessage,
  sendSMS,
  writeErrorLog,
} from "../services/soar";

function CrisisReferral() {
  const { id } = useParams();
  const [referral, setReferral] = useState({});
  const [client, setClient] = useState({});
  const [showResponders, setShowResponders] = useState(false);
  const [selectedResponder, setSelectedResponder] = useState({});
  const [responders, setResponders] = useState([]);
  const [addingResponder, setAddingResponder] = useState(false);
  const [updatingResponse, setUpdatingResponse] = useState(false);
  const [responderData, setResponderData] = useState({});
  const navigate = useNavigate();

  const responderOptions = useMemo(() => {
    if (!referral.responders) {
      return responders;
    }

    const selected = referral.responders
      .filter((r) => r.status !== "Declined")
      .map((r) => r.responderId.toUpperCase());
    return responders.filter((r) => !selected.includes(r._id.toUpperCase()));
  }, [referral.responders, responders]);

  const getReferral = async (referralId) => {
    if (referralId) {
      const resp = await crisisReferrals({
        command: "query",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
            : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
        keyword: "byid",
        ids: [referralId],
      });

      if (resp?.code === "success" && resp.data?.[0]) {
        setReferral(resp.data[0]);
        toast.success("Referral Loaded");
      } else {
        toast.error("Error Retrieving Referral");
      }
    }
  };

  useEffect(() => {
    getMobileCrisisUsers();
  }, []);

  useEffect(() => {
    getReferral(id);
  }, [id]);

  useEffect(() => {
    if (referral.clientId) {
      const getClient = async () => {
        const data = await getContact(referral.clientId);
        setClient(data[0]);
        let obj;

        setReferral((r) => {
          if (r.clientName !== data[0].fullname) {
            obj = { ...r };
            obj.clientName = data[0].fullname;
            obj.clientFirstName = data[0].firstname;
            obj.clientLastName = data[0].lastname;
            return obj;
          }

          return r;
        });

        if (obj) {
          delete obj.responders;
          crisisReferrals(obj);
        }
      };

      getClient();
    }
  }, [referral.clientId]);

  const getMobileCrisisUsers = async () => {
    const resp = await reactManagementUsers({
      command: "query",
      application:
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
          : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
      keyword: "mobilecrisisnotadmin",
    });
    if (resp?.code === "success") {
      // console.log("Mobile Crisis User", resp);
      let users = resp.data.map((u) => ({
        key: u.user.id,
        _id: u.user.id,
        name: u.user.name,
        email: u.user.email,
        phone: u.user.phone,
      }));

      if (process.env.REACT_APP_ENVIRONMENT !== "Production") {
        users.push({
          _id: "A9FB2D2D-6E73-4ECF-8B15-F375EBE32427",
          name: "Cameron Hiebert",
          email: "hiebertc@safy.org",
          phone: "419-371-8560",
        });
        users.push({
          _id: "8ABB1C24-10C6-40F3-97DA-BF321719062E",
          name: "Kathryn Huggins",
          email: "maxsonk@safy.org",
          phone: "419-234-6579",
        });
        users.push({
          _id: "52CC8216-EF55-4BA8-B3E7-59C084C13915",
          name: "George Oldja",
          email: "oldjag@safy.org",
          phone: "941-545-3517",
        });
      }

      if (process.env.REACT_APP_ENVIRONMENT === "Development") {
        users.push({
          _id: "925B5E6C-190B-459A-ADEA-AFF8E82AFC14",
          name: "Jeremy Pippen",
          email: "pippenj@safy.org",
          phone: "704-798-6234",
        });
      }

      setResponders(users.sort((a, b) => a.name.localeCompare(b.name)));
    } else {
      toast.error("Error Retrieving Mobile Crisis Users");
    }
  };

  const saveReferral = async (referralOverride = {}) => {
    const obj = {
      ...referral,
      ...referralOverride,
      command: "update",
    };
    console.log("saveReferral", referralOverride);

    if (
      obj.notDispatchedReason !== "" &&
      obj.notDispatchedReason !== undefined
    ) {
      obj.status = "Not Dispatched";
    }

    delete obj.responders;
    const resp = await crisisReferrals(obj);

    if (resp?.code === "success") {
      toast.success("Referral updated successfully");
    } else {
      toast.error("Error Updating Referral");
    }

    return resp;
  };

  const saveResponder = async () => {
    if (addingResponder === false) {
      setAddingResponder(true);

      const query = {
        command: "query",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
            : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
        keyword: "byid",
        ids: [id],
      };

      const queryResponse = await crisisReferrals(query);

      if (queryResponse?.code === "success") {
        const latestReferralData = queryResponse.data[0];

        if (!latestReferralData.active) {
          setReferral(latestReferralData);
          setShowResponders(false);
          setAddingResponder(false);
          confirmAlert({
            title: (
              <h5>
                <i className="fas fa-triangle-exclamation me-2 text-danger" />
                Action not permitted
              </h5>
            ),
            message: (
              <>
                The referral has been deactivated.
                <br />
                You can no longer assign Responders.
              </>
            ),
            closeOnClickOutside: false,
            buttons: [
              {
                label: "Close",
                onClick: () => {
                  /* do nothing */
                },
              },
            ],
          });

          return;
        }
      } else {
        setAddingResponder(false);
        toast.error("An error occurred attempting to get latest referral info");
        return;
      }
      const body = {
        command: "insert",
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
            : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
        crisisReferral: id,
        responderId: selectedResponder.id,
        soarUserId: "",
        responderName: selectedResponder.name,
        modifiedBy: JSON.parse(localStorage.getItem("safyInfo")).user.azureId,
      };

      const resp = await crisisResponder(body);

      if (resp?.code === "success") {
        setShowResponders(false);
        setAddingResponder(false);
        //send message to responder

        sendSMS({
          to: selectedResponder.phone,
          body: `You have received a crisis referral ${window.location.href.replace(
            "localhost:3000",
            "safyportaltest.safy.org"
          )}${
            referral.location
              ? `

${referral.location}

https://www.google.com/maps?q=${referral.location
                  .replaceAll(" ", "+")
                  .replaceAll("\n", "+")}`
              : ""
          }`,
        });
        if (referral.status === "New") {
          referral.status = "Pending";
          console.log("Setting Referral to Pending", referral);
        }
        await saveReferral();
        getReferral(id);
      } else {
        toast.error("Error Adding Responder", resp?.message);
        setAddingResponder(false);
      }
    }
  };

  const responderResponse = async (responder, response) => {
    if (updatingResponse === false) {
      setUpdatingResponse(true);
      let latestReferralData;
      let latestResponderData;

      // if response is OnScene, retrieve latest referral data to prevent duplicate program admissions
      if (response === "OnScene") {
        const query = {
          command: "query",
          application:
            process.env.REACT_APP_ENVIRONMENT === "Production"
              ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
              : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
          keyword: "byid",
          ids: [id],
        };

        const queryResponse = await crisisReferrals(query);

        if (queryResponse?.code === "success") {
          latestReferralData = queryResponse.data[0];
        } else {
          // if API fails to retrieve latest referral data, show toast and prevent action
          toast.error("An error occurred when verifying referral status.");
          return;
        }
      }

      // if response is Deactivate, retrieve latest responder data to make sure responder hasn't Accepted already
      if (response === "Deactivate") {
        const query = {
          command: "query",
          application:
            process.env.REACT_APP_ENVIRONMENT === "Production"
              ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
              : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
          keyword: "byid",
          ids: [responder.id],
        };

        const queryResponse = await crisisResponder(query);

        if (queryResponse?.code === "success") {
          latestResponderData = queryResponse.data[0];
          if (latestResponderData.status !== "Pending") {
            const obj = { ...referral };
            const index = obj.responders.findIndex(
              (res) => res.id === responder.id
            );
            obj.responders[index] = latestResponderData;
            setReferral(obj);
            toast.warning(
              `Unable to remove because the Responder status is ${latestResponderData.status}`
            );
            return;
          }
        } else {
          // if API fails to retrieve latest referral data, show toast and prevent action
          toast.error("An error occurred when verifying responder status.");
          return;
        }
      }

      let body = {
        command: response.toLowerCase(),
        id: responder.id,
        soarUserId: JSON.parse(localStorage.getItem("safyInfo")).user.safyInfo
          .SOARUserId,
        responderId: responder.responderId,
        responderName: responder.responderName,
        application:
          process.env.REACT_APP_ENVIRONMENT === "Production"
            ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
            : "58990f83-8566-4ba8-b4b5-584a64e4cba1",
        declineReason: responder.declineReason,
        deactivateReason: responder.deactivateReason,
      };
      const responderResponse = await crisisResponder(body);
      console.log("Responder Response api", responderResponse);
      if (responderResponse?.code === "success") {
        if (response === "Accept") {
          const obj = { ...referral };

          if (referral.status === "New" || referral.status === "Pending") {
            obj.status = "Partially Accepted";
          }

          const index = obj.responders.findIndex(
            (res) => res.id === responder.id
          );
          obj.responders[index].status = "Accepted";
          setReferral(obj);
          console.log("Setting Referral to Partially Accepted", obj);
          const referralResponse = await saveReferral(obj);

          if (referralResponse?.code === "success") {
            toast.success("Responder Accepted");
          } else {
            const safyUser = JSON.parse(localStorage.getItem("safyInfo")).user;
            const org =
              process.env.REACT_APP_ENVIRONMENT === "Production"
                ? "ReactLive"
                : "ReactDev";
            const logData = {
              Who: `${safyUser.domainName}|${safyUser.displayName}`,
              FullError: JSON.stringify(referralResponse),
              API: "Zeto API",
              APIBody: JSON.stringify(obj),
              URL: "https://safyzetoapi.azurewebsites.net/crisis/referrals",
              Org: org,
              ResponseCode: referralResponse?.code,
              ResponseMessage: referralResponse?.message,
              ExceptionType: null,
              Application: "React Portal/Mobile Crisis",
            };

            writeErrorLog(logData);
            sendEmailMessage(
              ["hiebertc@safy.org", "pippenj@safy.org"],
              null,
              "Crisis Referral status update failed",
              `The API call to update the Crisis Referral status failed`
            );
          }
        }

        if (response === "OnScene") {
          if (!latestReferralData.clientProgram) {
            // if latest referral response does NOT have a client program, create records
            await createClinicalRecords(latestReferralData);
            return;
          } else {
            // else navigate to existing record
            navigate(`/client/${referral.clientId}`);
          }
        }

        if (response === "Decline") {
          const obj = { ...referral };
          const index = obj.responders.findIndex(
            (res) => res.id === responder.id
          );
          obj.responders[index].status = "Declined";
          obj.responders[index].declineReason = responder.declineReason;
          setReferral(obj);
        }

        if (response === "Deactivate") {
          if (latestResponderData.status === "Pending") {
            const obj = { ...referral };
            const index = obj.responders.findIndex(
              (res) => res.id === responder.id
            );
            obj.responders.splice(index, 1);
            setReferral(obj);
            toast.success("Responder removed successfully");
          }
        }
      } else {
        toast.error("Error processing responder response");
      }

      setUpdatingResponse(false);
    }
  };

  const createClinicalRecords = async (latestData) => {
    const toastId = toast.info("Creating Program Records");
    const programUnit = "17E95F81-1415-EA11-BCAC-000C295EBA08";

    const controller = new AbortController();
    const signal = controller.signal;
    const companyResp = await getClientCompanies(
      latestData.clientId,
      "SAFY of Colorado",
      signal
    );

    const clientProgramObj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: latestData.clientId,
        },
      },
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_clientagency",
          entityId: companyResp[0].ccx_clientagencyid
            .toString()
            .replaceAll("{", "")
            .replaceAll("}", ""),
        },
      },
      ccx_programunit: {
        type: "entity",
        value: {
          entityName: "ccx_unit",
          entityId: programUnit,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: new Date(DateTime.now().toISODate()),
      },
      sor_dischargelog: {
        type: "string",
        value: latestData.id,
      },
    };

    console.log("client program obj: ", clientProgramObj);

    const clientProgram = await createEntity(
      "ccx_client_program",
      JSON.stringify(clientProgramObj),
      JSON.parse(localStorage.getItem("safyInfo")),
      "Colorado"
    );

    toast.update(toastId, {
      render: "Program Records Created Successfully",
      type: toast.TYPE.SUCCESS,
      autoClose: 5000,
    });

    await latestData.responders.forEach((r, index) => {
      if (r.status === "Accepted") {
        const careteammemberObj = {
          ccx_client: {
            type: "entity",
            value: {
              entityName: "contact",
              entityId: latestData.clientId,
            },
          },
          ccx_clientcompanyid: {
            type: "entity",
            value: {
              entityName: "ccx_clientagency",
              entityId: companyResp[0].ccx_clientagencyid
                .toString()
                .replaceAll("{", "")
                .replaceAll("}", ""),
            },
          },
          ccx_clientprogram: {
            type: "entity",
            value: {
              entityName: "ccx_client_program",
              entityId: clientProgram.resultset.result.ccx_client_programid
                .toString()
                .replaceAll("{", "")
                .replaceAll("}", ""),
            },
          },
          ccx_membertype: {
            type: "optionset",
            value: "803080002",
          },
          ccx_effdate: {
            type: "datetime",
            value: new Date(),
          },
          ccx_expdate: {
            type: "datetime",
            value: null,
          },
          ccx_expirationreason: {
            type: "optionset",
            value: null,
          },
          ccx_role: {
            type: "optionset",
            value: "803080000",
          },

          ccx_primarymember: {
            type: "bool",
            value: index === 0 ? 1 : 0,
          },

          ccx_name: {
            type: "string",
            value: r.responderName,
          },

          ccx_memberid: {
            type: "entity",
            value: {
              entityName: "systemuser",
              entityId: r.soarUserId,
            },
          },

          sor_providerrole: {
            type: "optionset",
            value: "186460000",
          },
        };
        createEntity(
          "ccx_careteam",
          JSON.stringify(careteammemberObj),
          JSON.parse(localStorage.getItem("safyInfo")),
          "Colorado"
        )
          .then(() => {
            toast.success("Care Team Member has been created.");
          })
          .catch(() => {
            toast.error(
              "An error occurred while creating the Care Team Member."
            );
          });
      }
    });

    latestData.status = "Accepted";
    latestData.clientProgram =
      clientProgram.resultset.result.ccx_client_programid;

    setReferral(latestData);
    const response = await saveReferral(latestData);

    if (response?.code === "success") {
      navigate(`/client/${referral.clientId}`);
    } else {
      const safyUser = JSON.parse(localStorage.getItem("safyInfo")).user;
      const org =
        process.env.REACT_APP_ENVIRONMENT === "Production"
          ? "ReactLive"
          : "ReactDev";
      const logData = {
        Who: `${safyUser.domainName}|${safyUser.displayName}`,
        FullError: JSON.stringify(response),
        API: "Zeto API",
        APIBody: JSON.stringify(latestData),
        URL: "https://safyzetoapi.azurewebsites.net/crisis/referrals",
        Org: org,
        ResponseCode: response?.code,
        ResponseMessage: response?.message,
        ExceptionType: null,
        Application: "React Portal/Mobile Crisis",
      };

      writeErrorLog(logData);
      sendEmailMessage(
        ["hiebertc@safy.org", "pippenj@safy.org"],
        null,
        "Crisis Referral link to Client Program failed",
        `The API call to update the Crisis Referral with the new created Client Program Id failed`
      );
    }
  };

  const handleChange = ({ target: input }) => {
    setReferral((r) => ({
      ...r,
      [input.name]: input.type === "checkbox" ? input.checked : input.value,
    }));
  };

  const isLoggedInResponder = (r) => {
    return (
      r.responderId.toUpperCase() ===
      JSON.parse(localStorage.getItem("safyInfo")).user.azureId.toUpperCase()
    );
  };

  const clearResponderData = () => {
    setResponderData({});
  };

  useEffect(() => {
    if (!referral.notDispatchedReason) {
      setReferral((r) => ({ ...r, notDispatchedReasonExplanation: "" }));
    }
  }, [referral.notDispatchedReason]);

  const formDisabled = useMemo(() => {
    return referral.status === "Closed";
  }, [referral.status]);

  return (
    <>
      {responderData.command === "Decline" ? (
        <Modal id="decline_modal" show={true} animation={false}>
          <Modal.Header className="bg-safy-info text-white">
            <Modal.Title>Decline Crisis Referral</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-safy2-4">
            <div className="fw-bold">{responderData.clientName}</div>
            <div className="fw-bold mb-3">
              {moment(responderData.referralDateTime).format("ll LT")}
            </div>
            <Select
              name="decline_reason"
              label="Decline Reason"
              value={responderData.declineReason}
              options={[
                {
                  _id: "Client Denied Service/Withdrew Request",
                  name: "Client Denied Service/Withdrew Request",
                },
                {
                  _id: "Client Presentation Changed/De-escalated",
                  name: "Client Presentation Changed/De-escalated",
                },
                {
                  _id: "Inadequate Staffing",
                  name: "Inadequate Staffing",
                },
                {
                  _id: "Safety - Client Needs Higher Level of Care",
                  name: "Safety - Client Needs Higher Level of Care",
                },
                { _id: "Weather", name: "Weather" },
              ]}
              onChange={({ currentTarget: input }) =>
                setResponderData((r) => ({ ...r, declineReason: input.value }))
              }
              error={responderData.declineError}
            />
          </Modal.Body>
          <Modal.Footer className="bg-safy2-4">
            <button className="btn btn-danger" onClick={clearResponderData}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (!responderData.declineReason) {
                  setResponderData((r) => ({
                    ...r,
                    error: "Reason must be provided",
                  }));
                } else {
                  const obj = { ...referral };
                  obj.status = "Not Dispatched";
                  setReferral(obj);
                  saveReferral(obj);
                  responderResponse(responderData, "Decline");
                  clearResponderData();
                }
              }}
            >
              Submit
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      {responderData.command === "Deactivate" ? (
        <Modal id="deactivate_modal" show={true} animation={false}>
          <Modal.Header className="bg-safy-info text-white">
            <Modal.Title>Remove Responder</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-safy2-4">
            <div className="fw-bold mb-3">
              {responderData.responderName} | {responderData.responderStatus}
            </div>
            <TextArea
              name="deactivate_reason"
              label="Reason"
              value={responderData.deactivateReason}
              onChange={({ currentTarget: input }) =>
                setResponderData((r) => ({
                  ...r,
                  deactivateReason: input.value,
                }))
              }
              error={responderData.deactivateError}
              maxCharacters={500}
            />
          </Modal.Body>
          <Modal.Footer className="bg-safy2-4">
            <button className="btn btn-danger" onClick={clearResponderData}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (!responderData.deactivateReason) {
                  setResponderData((r) => ({
                    ...r,
                    error: "Reason must be provided",
                  }));
                } else {
                  responderResponse(responderData, "Deactivate");
                  clearResponderData();
                }
              }}
            >
              Submit
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
      <Modal
        show={showResponders}
        onHide={() => {
          setSelectedResponder({});
          setShowResponders(false);
        }}
        animation={false}
        size="lg"
      >
        <Modal.Header closeButton className="bg-safy-info text-white">
          <Modal.Title>Add Responder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FloatingInput
            type="select"
            name="responder"
            label="Responder"
            value={selectedResponder.id}
            options={responderOptions}
            onChange={(e) =>
              setSelectedResponder({
                ...selectedResponder,
                id: e.currentTarget.value,
                name:
                  responderOptions.find((r) => r._id === e.currentTarget.value)
                    .name || "",
                phone:
                  responderOptions.find((r) => r._id === e.currentTarget.value)
                    .phone || "",
                email:
                  responderOptions.find((r) => r._id === e.currentTarget.value)
                    .email || "",
              })
            }
          />
          {selectedResponder.phone === "" ? (
            <div className="mt-2 alert alert-danger">
              Responder does not have a Mobile Phone # configured in Active
              Directory.
              <br />
              Please email support@safy.org.
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="bg-safy2-4">
          <button
            className="btn btn-danger btn-sm"
            onClick={() => {
              setSelectedResponder({});
              setShowResponders(false);
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              saveResponder();
            }}
            disabled={addingResponder}
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>
      {!formDisabled ? (
        <div className="floating-toolbox">
          <div className="clearfix mb-3">
            <div className="float-start"></div>
            <div className="text-center" onClick={() => saveReferral()}>
              <i
                className="far fa-save fa-2x floating-button floating-save rounded-circle pt-2 text-white"
                style={{ backgroundColor: "#28a745" }}
              />
            </div>
          </div>
        </div>
      ) : null}
      <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 mb-2">
        <div className="col">
          <div className="rounded border border-primary card-safy bg-light p-2">
            {referral.clientProgram ? (
              <div
                className="btn btn-primary"
                onClick={() => navigate(`/client/${referral.clientId}`)}
                title={`${client.firstname} ${client.lastname}-${client.ccx_mpistr}`}
              >
                {`${client.lastname}, ${client.firstname}`}
              </div>
            ) : (
              <div>
                <i
                  className="fas fa-user-circle text-primary me-2"
                  role="button"
                  onClick={() => navigate(`/contact/${referral.clientId}`)}
                  title={`${client.firstname} ${client.lastname}-${client.ccx_mpistr}`}
                ></i>
                <span className="fw-bold">{`${client.lastname}, ${client.firstname}`}</span>
              </div>
            )}
            <div>
              <span>MPI: </span>
              <span className="fw-bold">{client.ccx_mpistr}</span>
            </div>
            <div>
              <span>DOB: </span>
              <span className="fw-bold">
                {client.birthdate && client.birthdate["@date"]}
              </span>
            </div>
            <div>
              <span>Mobile: </span>
              <span className="fw-bold">{client.mobilephone || ""}</span>
            </div>
          </div>
        </div>
        <div className="col">
          Referral Date/Time
          <div className="h5 mb-4">
            {moment(referral.referralDateTime).format("LLL")}
          </div>
          <div>
            Status: <span className="fw-bold">{referral.status}</span>
          </div>
          <div className="mt-2">
            Created by:{" "}
            <span className="fw-bold">{referral.createdByName}</span>
          </div>
        </div>
        <div className="col">
          <FloatingInput
            className="border border-dark"
            name="chart"
            label="Chart #"
            value={referral.chart || ""}
            onChange={handleChange}
            disabled={formDisabled}
          />
          <FloatingInput
            type="datetime-local"
            className="border border-dark"
            name="actualDispatchDateTime"
            label="Actual Dispatch Date/Time"
            value={referral.actualDispatchDateTime || ""}
            onChange={handleChange}
            disabled={formDisabled}
          />
        </div>
        <div className="col">
          <FloatingInput
            className="border border-dark"
            type="select"
            name="medicaid"
            label="Medicaid"
            value={referral.medicaid || ""}
            options={[
              { name: "Yes", _id: "Yes" },
              { name: "No", _id: "No" },
            ]}
            onChange={handleChange}
            disabled={formDisabled}
          />
          {referral.medicaid === "Yes" && (
            <FloatingInput
              className="border border-dark"
              name="medicaidNumber"
              label="Medicaid Number"
              value={referral.medicaidNumber || ""}
              onChange={handleChange}
              disabled={formDisabled}
            />
          )}
        </div>
      </div>
      <div className="container">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-4 rounded bg-safy2-1 py-2 mb-2">
          <div className="col">
            <FloatingInput
              type="select"
              name="referralSource"
              label="Referral Source"
              value={referral.referralSource || ""}
              onChange={handleChange}
              options={[
                { name: "CO Crisis Hotline", _id: "CO Crisis Hotline" },
                { name: "CO Crisis Community", _id: "CO Crisis Community" },
                {
                  name: "CO Crisis Self Referral",
                  _id: "CO Crisis Self Referral",
                },
                {
                  name: "CO Crisis Law Enforcement",
                  _id: "CO Crisis Law Enforcement",
                },
                {
                  name: "CO Crisis Child Welfare Family",
                  _id: "CO Crisis Child Welfare Family",
                },
                {
                  name: "CO Crisis Criminal Justice System",
                  _id: "CO Crisis Criminal Justice System",
                },
              ]}
              disabled={formDisabled}
            />
          </div>
          <div className="col">
            <FloatingInput
              name="dispatcher"
              label="Dispatcher"
              value={referral.dispatcher || ""}
              onChange={handleChange}
              disabled={formDisabled}
            />
          </div>
          <div className="col">
            <FloatingInput
              type="select"
              name="serviceArea"
              label="Service Area"
              value={referral.serviceArea || ""}
              onChange={handleChange}
              options={[
                { name: "Service Area 1", _id: "Service Area 1" },
                { name: "Service Area 2", _id: "Service Area 2" },
                { name: "Other", _id: "Other" },
              ]}
              disabled={formDisabled}
            />
          </div>
          <div className="col">
            {referral.serviceArea === "Other" && (
              <FloatingInput
                name="serviceAreaOther"
                label="Service Area Other"
                value={referral.serviceAreaOther || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            )}
          </div>
        </div>
      </div>
      <ExpandDiv
        title="Referral Info"
        expandStatus={true}
        expandIcon="fa-regular fa-eye text-primary"
        collapseIcon="fa-regular fa-eye-slash text-danger"
      >
        <div className="container rounded border border-dark bg-light">
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 py-2">
            <div className="col">
              <div className="form-label row justify-content-between">
                <div className="col-auto">Address</div>
                <div className="col-auto">
                  {referral.location && (
                    <i
                      className="fa fa-map-pin text-danger"
                      onClick={() =>
                        window.open(
                          `https://maps.google.com/?q=${referral.location
                            .replaceAll(" ", "+")
                            .replaceAll("\n", "+")}`,
                          "_blank"
                        )
                      }
                    ></i>
                  )}
                </div>
              </div>
              <TextArea
                className="border border-dark"
                type="textarea"
                name="location"
                value={referral.location || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                type="textarea"
                name="locationNotes"
                label="Location Notes"
                value={referral.locationNotes || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="personFacilitatingIntroduction"
                label="Person Facilitating Introduction"
                value={referral.personFacilitatingIntroduction || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <div className="ps-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="aware"
                  label="Aware/Agreement with Mobile Response"
                  checked={referral.aware === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
            </div>
            <div className="col">
              <TextArea
                className="border border-dark"
                type="textarea"
                name="clientDescription"
                label="Client Description"
                value={referral.clientDescription || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                type="textarea"
                name="presentingConcern"
                label="Description of Presenting Concern"
                value={referral.presentingConcern || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="col-12 col-md-4">
              <div className="rounded border border-dark bg-safy2-4 p-2">
                <div className="fw-bold mb-2">Clinical Issues and Goals</div>
                <div className="px-4">
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="cssrs"
                    label="C-SSRS"
                    checked={referral.cssrs === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                </div>
                <div className="px-4">
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="pointAssessment"
                    label="18 Pt Assessment"
                    checked={referral.pointAssessment === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="crisisAssessment"
                    label="Crisis Assessment"
                    checked={referral.crisisAssessment === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="safetyPlan"
                    label="Safety Plan"
                    checked={referral.safetyPlan === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="hloc"
                    label="Assess for HLOC"
                    checked={referral.hloc === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="resources"
                    label="Resources"
                    checked={referral.resources === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  <Checkbox
                    style={{ borderColor: "black" }}
                    name="goalOther"
                    label="Other"
                    checked={referral.goalOther === true}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  {referral.goalOther === true && (
                    <FloatingInput
                      className="border border-dark"
                      name="goalOtherText"
                      label="Describe"
                      value={referral.goalOtherText || ""}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ExpandDiv>
      <ExpandDiv
        className="my-4"
        title="Responders"
        expandStatus={true}
        expandIcon="fa-regular fa-eye text-primary"
        collapseIcon="fa-regular fa-eye-slash text-danger"
      >
        <div className="container rounded border border-dark bg-light">
          <div className="row mt-2">
            <div className="col-7 col-lg-5 col-xxl-4">
              <div className="h5">Responders Needed</div>
              <div className="h6 mt-0">
                <TrueInput
                  className="border border-dark"
                  name="respondersNeeded"
                  value={referral.respondersNeeded || ""}
                  onChange={handleChange}
                  type="number"
                  disabled={formDisabled}
                />
              </div>
            </div>
            <div className="col">
              <div className="h5">
                <div>Responders Accepted</div>
                <div className="ms-2 mt-2">
                  {
                    referral.responders?.filter((r) => r.status === "Accepted")
                      .length
                  }
                </div>
              </div>
            </div>
            <div className="col-auto">
              {!formDisabled ? (
                <i
                  className="fas fa-plus-circle fa-2x text-primary float-end"
                  onClick={() => {
                    setSelectedResponder({
                      id: "",
                      name: "",
                      status: "Pending",
                      responseDateTime: "",
                      onSceneDateTime: "",
                    });
                    setShowResponders(true);
                  }}
                ></i>
              ) : null}
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-7 col-lg-5 col-xxl-4">
              <div className="d-flex">
                <div className="h5 me-2">Not Dispatched Reason</div>
                <span className="align-middle">
                  <i
                    className="fas fa-circle-info fa-lg"
                    title="Select a reason if the referral was not dispatched to responders"
                  />
                </span>
              </div>
              <Select
                name="notDispatchedReason"
                value={referral.notDispatchedReason}
                options={[
                  {
                    _id: "Client Denied Service/Withdrew Request",
                    name: "Client Denied Service/Withdrew Request",
                  },
                  {
                    _id: "Client Presentation Changed/De-escalated",
                    name: "Client Presentation Changed/De-escalated",
                  },
                  {
                    _id: "Inadequate Staffing",
                    name: "Inadequate Staffing",
                  },
                  {
                    _id: "Safety - Client Needs Higher Level of Care",
                    name: "Safety - Client Needs Higher Level of Care",
                  },
                  { _id: "Weather", name: "Weather" },
                ]}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="col">
              <div className="h5">Not Dispatched Explanation</div>
              <TextArea
                name="notDispatchedReasonExplanation"
                value={referral.notDispatchedReasonExplanation}
                onChange={(e) => {
                  const value = e.currentTarget.value;

                  if (!value || value.length <= 500) {
                    handleChange(e);
                  }
                }}
                disabled={formDisabled || !referral.notDispatchedReason}
                maxCharacters="500"
              />
            </div>
          </div>
          {referral.responders?.map((responder) => (
            <div
              key={responder.id}
              className={`row rounded border border-dark bg-safy2-4 fw-bold p-2 my-2 mx-4`}
            >
              <div className="col">
                {responder.responderName}
                {!responders.find((r) => r._id === responder.responderId)
                  ?.phone ? (
                  <i
                    className="fas fa-phone-slash ms-2 text-danger"
                    title={`Responder does not have a Mobile Phone # configured in Active Directory.\nPlease email support@safy.org.`}
                  />
                ) : null}
              </div>
              <div className="col">
                <div className="float-start float-sm-end">
                  {responder.status === "Accepted" ? (
                    <>
                      <div className="clearfix">
                        <div className="float-end">
                          Accepted{" "}
                          {DateTime.fromISO(
                            responder.responseDateTime
                          ).toLocaleString(DateTime.DATETIME_MED)}
                        </div>
                      </div>
                      {responder.onSceneDateTime ? (
                        <div className="clearfix">
                          <div className="float-end">
                            Arrived on Scene{" "}
                            {DateTime.fromISO(
                              responder.onSceneDateTime
                            ).toLocaleString(DateTime.DATETIME_MED)}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    responder.status
                  )}
                  {responder.declineReason
                    ? ` | ${responder.declineReason}`
                    : null}
                </div>
              </div>
              {responder.status === "Pending" ? (
                <div className="col-auto">
                  <div className="d-inline-block">
                    {isLoggedInResponder(responder) ? (
                      <>
                        <div
                          className="btn btn-sm border border-white btn-success me-2"
                          onClick={() => responderResponse(responder, "Accept")}
                        >
                          Accept
                        </div>
                        <div
                          className="btn btn-sm btn-danger border border-white me-4"
                          onClick={() =>
                            setResponderData({
                              id: responder.id,
                              command: "Decline",
                              clientName: referral.clientName,
                              referralDateTime: referral.referralDateTime,
                              responderId: responder.responderId,
                              responderName: responder.responderName,
                            })
                          }
                        >
                          Decline
                        </div>
                      </>
                    ) : null}
                    <i
                      className="fas fa-trash-alt fa-lg text-primary"
                      onClick={() =>
                        setResponderData({
                          id: responder.id,
                          command: "Deactivate",
                          clientName: referral.clientName,
                          referralDateTime: referral.referralDateTime,
                          responderId: responder.responderId,
                          responderName: responder.responderName,
                          responderStatus: responder.status,
                        })
                      }
                    />
                  </div>
                </div>
              ) : null}
              {isLoggedInResponder(responder) &&
              responder.status === "Accepted" &&
              !responder.onSceneDateTime ? (
                <div className="col-auto">
                  <div className="d-inline-block">
                    {!updatingResponse ? (
                      <div
                        className="btn btn-sm border border-white btn-success me-2"
                        onClick={() => responderResponse(responder, "OnScene")}
                        disabled={updatingResponse}
                      >
                        Arrived on Scene
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </ExpandDiv>
      <ExpandDiv
        title="Scene Info"
        expandStatus={true}
        expandIcon="fa-regular fa-eye text-primary"
        collapseIcon="fa-regular fa-eye-slash text-danger"
      >
        <div className="row row-cols-1 row-cols-lg-3">
          <div className="col">
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5 ">Safety Concerns</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="environmentalConcerns"
                  label="Environmental Concerns"
                  checked={referral.environmentalConcerns === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="gunsWeapons"
                  label="Guns or Weapons on Scene"
                  checked={referral.gunsWeapons === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="animalsPresent"
                  label="Animals Present on Scene"
                  checked={referral.animalsPresent === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="domesticViolence"
                  label="Domestic Violence"
                  checked={referral.domesticViolence === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="aggression"
                  label="Aggression"
                  checked={referral.aggression === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="suicidalBehavior"
                  label="Suicidal Behavior/Threats"
                  checked={referral.suicidalBehavior === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="homicidalBehavior"
                  label="Homicidal Behavior/Threats"
                  checked={referral.homicidalBehavior === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="psychosis"
                  label="Psychosis"
                  checked={referral.psychosis === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="intoxication"
                  label="Intoxication"
                  checked={referral.intoxication === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="withdrawal"
                  label="Withdrawal"
                  checked={referral.withdrawal === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>

              <TextArea
                className="border border-dark"
                name="safetyConcernsComments"
                label="Comments"
                value={referral.safetyConcernsComments || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Medical Issues/Concerns</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="reportedMedicalIssues"
                  label="Reported Medical Issues"
                  checked={referral.reportedMedicalIssues === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="needMedicalAttention"
                  label="Need for Medical Attention/Hospitalization"
                  checked={referral.needMedicalAttention === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="illness"
                  label="Illness"
                  checked={referral.illness === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="currentMedications"
                  label="Current Medications"
                  checked={referral.currentMedications === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="graveDisability"
                  label="Grave Disability"
                  checked={referral.graveDisability === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
              <TextArea
                className="border border-dark"
                name="medicalComments"
                label="Comments"
                value={referral.medicalComments || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Substance Use</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="currentSubstanceUse"
                  label="Current Substance Use"
                  checked={referral.currentSubstanceUse === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="reportedSubstanceAbuse"
                  label="History/Current Reported Substance Abuse"
                  checked={referral.reportedSubstanceAbuse === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="needDetox"
                  label="Need for Medical Attention/Detox"
                  checked={referral.needDetox === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
              <TextArea
                className="border border-dark"
                name="substanceUseComments"
                label="Comments"
                value={referral.substanceUseComments || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
          </div>
          <div className="col">
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Secondary Responder Type</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="caseManager"
                  label="Case Manager"
                  checked={referral.caseManager === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="licensedClinician"
                  label="Licensed Clinician"
                  checked={referral.licensedClinician === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="unlicensedClinician"
                  label="Unlicensed Clinician"
                  checked={referral.unlicensedClinician === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="peer"
                  label="Peer"
                  checked={referral.peer === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="emt"
                  label="EMT"
                  checked={referral.emt === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="otherResponder"
                  label="Other"
                  checked={referral.otherResponder === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
              <TextArea
                className="border border-dark"
                name="secondaryResponderComments"
                label="Comments"
                value={referral.secondaryResponderComments || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Professionals on Scene</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="lawEnforcement"
                  label="Law Enforcement"
                  checked={referral.lawEnforcement === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="childAdultProtectiveServices"
                  label="Child/Adult Protective Services"
                  checked={referral.childAdultProtectiveServices === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="emsFire"
                  label="EMS/Fire"
                  checked={referral.emsFire === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="otherMentalHealth"
                  label="Other Mental Health"
                  checked={referral.otherMentalHealth === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="others"
                  label="Others"
                  checked={referral.others === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
              <TextArea
                className="border border-dark"
                name="professionalsOnSceneComments"
                label="Comments"
                value={referral.professionalsOnSceneComments || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
            </div>
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Other Scene Information</div>
              <div className="ms-4">
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="idd"
                  label="IDD"
                  checked={referral.idd === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <Checkbox
                  style={{ borderColor: "black" }}
                  name="veteran"
                  label="Veteran"
                  checked={referral.veteran === true}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              </div>
            </div>
          </div>
          <div className="col">
            <div className="px-4 rounded border border-dark bg-light p-2 mb-2">
              <div className="h5">Ecosystem</div>
              <TextArea
                className="border border-dark"
                name="timeOfDay"
                label="Time of Day"
                value={referral.timeOfDay || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="weatherCondition"
                label="Weather Conditions"
                value={referral.weatherCondition || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="roadConditionsTraffic"
                label="Road Conditions/Traffic"
                value={referral.roadConditionsTraffic || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="facilityHomeCommunity"
                label="Facility/Home/Community"
                value={referral.facilityHomeCommunity || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="highCrimeArea"
                label="High Crime Area"
                value={referral.highCrimeArea || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <TextArea
                className="border border-dark"
                name="parkingScenarios"
                label="Parking Scenarios"
                value={referral.parkingScenarios || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <Checkbox
                style={{ borderColor: "black" }}
                name="cellularConnectivity"
                label="Cellular Connectivity"
                checked={referral.cellularConnectivity === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <Checkbox
                style={{ borderColor: "black" }}
                name="internetConnectivity"
                label="Internet Connectivity"
                checked={referral.internetConnectivity === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <Checkbox
                style={{ borderColor: "black" }}
                name="entranceExitBarriers"
                label="Entrance and Exit Barriers Outside of Home"
                checked={referral.entranceExitBarriers === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.entranceExitBarriers === true && (
                <TextArea
                  className="border border-dark"
                  name="entranceExitBarriersComments"
                  label="Describe"
                  value={referral.entranceExitBarriersComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Checkbox
                style={{ borderColor: "black" }}
                name="walkingHazards"
                label="Walking Hazards and Distance to Vehicle"
                checked={referral.walkingHazards === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.walkingHazards === true && (
                <TextArea
                  className="border border-dark"
                  name="walkingHazardsComments"
                  label="Describe"
                  value={referral.walkingHazardsComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Checkbox
                style={{ borderColor: "black" }}
                name="personsOutsideClient"
                label="Persons Outside of Client on Scene"
                checked={referral.personsOutsideClient === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.personsOutsideClient === true && (
                <TextArea
                  className="border border-dark"
                  name="personsOutsideClientComments"
                  label="Describe"
                  value={referral.personsOutsideClientComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Checkbox
                style={{ borderColor: "black" }}
                name="homeConditionHazards"
                label="Conditions of Home That May Create Hazards"
                checked={referral.homeConditionHazards === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.homeConditionHazards === true && (
                <TextArea
                  className="border border-dark"
                  name="homeConditionHazardsComments"
                  label="Describe"
                  value={referral.homeConditionHazardsComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Select
                style={{ borderColor: "black" }}
                name="weaponsInHome"
                label="Weapons in Home"
                value={referral.weaponsInHome}
                options={[
                  { name: "Yes", _id: "Yes" },
                  { name: "No", _id: "No" },
                  { name: "Unknown", _id: "Unknown" },
                ]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.weaponsInHome === "Yes" && (
                <TextArea
                  className="border border-dark"
                  name="weaponsInHomeComments"
                  label="Describe"
                  value={referral.weaponsInHomeComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Checkbox
                style={{ borderColor: "black" }}
                name="otherPersonsInHome"
                label="Other Persons in Home"
                checked={referral.otherPersonsInHome === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.otherPersonsInHome === true && (
                <TextArea
                  className="border border-dark"
                  name="otherPersonsInHomeComments"
                  label="Describe"
                  value={referral.otherPersonsInHomeComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}
              <Checkbox
                style={{ borderColor: "black" }}
                name="petsHomeYard"
                label="Pets in Home/Yard"
                checked={referral.petsHomeYard === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.petsHomeYard === true && (
                <TextArea
                  className="border border-dark"
                  name="petsHomeYardComments"
                  label="Describe"
                  value={referral.petsHomeYardComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <TextArea
                className="border border-dark"
                name="wildfireNoxiousInsects"
                label="Wildfire/Noxious Plants/Insects"
                value={referral.wildfireNoxiousInsects || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />

              <TextArea
                className="border border-dark"
                name="fleasBedBugsLiceTicksMites"
                label="Fleas/BedBugs/Lice/Ticks/Mites"
                value={referral.fleasBedBugsLiceTicksMites || ""}
                onChange={handleChange}
                disabled={formDisabled}
              />

              <Checkbox
                style={{ borderColor: "black" }}
                name="infectiousDisease"
                label="Infectious Disease"
                checked={referral.infectiousDisease === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.infectiousDisease === true && (
                <TextArea
                  className="border border-dark"
                  name="infectiousDiseaseComments"
                  label="Describe"
                  value={referral.infectiousDiseaseComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}

              <Checkbox
                style={{ borderColor: "black" }}
                name="entranceExitBarriersInside"
                label="Entrance and Exit Barriers Inside of Home"
                checked={referral.entranceExitBarriersInside === true}
                onChange={handleChange}
                disabled={formDisabled}
              />
              {referral.entranceExitBarriersInside === true && (
                <TextArea
                  className="border border-dark"
                  name="entranceExitBarriersInsideComments"
                  label="Describe"
                  value={referral.entranceExitBarriersInsideComments || ""}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
              )}
            </div>
          </div>
        </div>
      </ExpandDiv>
    </>
  );
}

export default CrisisReferral;
